<div class="page-header">
  <div class="page-title" data-clarity-unmask="true">Awarie</div>
  <div class="page-subtitle">
    wiadomości z ekranu prac konserwacyjnych w aplikacji i powiadomienia o usunięciu awarii
  </div>
</div>

<div class="outage-reports">
  <div class="flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
    <div class="flex align-items-center gap-2">
      <p-dropdown
        [options]="periodOptions"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="days"
        (onChange)="load()"
        styleClass="p-inputtext-sm"
      />
      @if (loading) {
        <span class="refresh-hint">odświeżanie…</span>
      }
    </div>
    <p-button label="Odśwież" icon="pi pi-refresh" size="small" [outlined]="true" (onClick)="refresh()" data-clarity-unmask="true" />
  </div>

  @if (errorMessage) {
    <div class="banner error">
      <i class="pi pi-exclamation-triangle"></i>
      <span>{{ errorMessage }}</span>
    </div>
  }

  @if (successMessage) {
    <div class="banner success">
      <i class="pi pi-check-circle"></i>
      <span>{{ successMessage }}</span>
    </div>
  }

  @if (reports?.databaseDown) {
    <div class="banner error">
      <i class="pi pi-database"></i>
      <span>
        Baza danych jest teraz niedostępna
        @if (reports?.databaseDownSince) {
          (od {{ reports?.databaseDownSince | date: 'yyyy-MM-dd HH:mm' }})
        }
        — wiadomość o usunięciu awarii wyślij dopiero po jej przywróceniu.
      </span>
    </div>
  }

  @if (reports?.emailLookupFailed) {
    <div class="banner warning">
      <i class="pi pi-info-circle"></i>
      <span>
        Części zgłoszeń od zalogowanych użytkowników nie udało się dopasować do adresów, bo baza danych nie
        odpowiedziała. Odśwież stronę, gdy baza wróci — te osoby dojdą do listy.
      </span>
    </div>
  }

  @if (loading && !reports) {
    <div class="flex justify-content-center py-5">
      <p-progressSpinner styleClass="w-3rem h-3rem" ariaLabel="loading" />
    </div>
  }

  @if (cards.length) {
    <div class="summary-grid">
      @for (card of cards; track card.label) {
        <div class="summary-card" [class.highlight]="card.highlight">
          <div class="summary-icon"><i [class]="card.icon"></i></div>
          <div class="summary-body">
            <div class="summary-label">{{ card.label }}</div>
            <div class="summary-value">{{ card.value }}</div>
            @if (card.detail) {
              <div class="summary-detail">{{ card.detail }}</div>
            }
          </div>
        </div>
      }
    </div>
  }

  @if (reports) {
    <div class="section-title">
      <i class="pi pi-send"></i>
      <span>Powiadomienie o usunięciu awarii</span>
    </div>

    <div class="mailing">
      <label for="outage-subject" class="field-label">Temat</label>
      <input id="outage-subject" pInputText [(ngModel)]="subject" [disabled]="!!status?.isRunning" />

      <label for="outage-body" class="field-label">Treść</label>
      <textarea id="outage-body" pInputTextarea rows="8" [(ngModel)]="body" [disabled]="!!status?.isRunning"></textarea>

      <div class="mailing-actions">
        <p-button
          [label]="'Wyślij do ' + reports.awaitingNotificationCount + ' ' + (reports.awaitingNotificationCount === 1 ? 'osoby' : 'osób')"
          icon="pi pi-send"
          [disabled]="!canSend"
          [loading]="starting"
          (onClick)="send()"
          data-clarity-unmask="true"
        />
        <p-button
          label="Przywróć domyślną treść"
          icon="pi pi-undo"
          [text]="true"
          size="small"
          [disabled]="!!status?.isRunning"
          (onClick)="resetMessage()"
          data-clarity-unmask="true"
        />
      </div>
      <div class="field-hint">
        Wiadomość dostaje każdy, kto zgłosił problem po ostatniej wiadomości od nas — zbiorczej albo odpowiedzi
        wysłanej tylko do niego (albo nie dostał jeszcze żadnej), w wybranym okresie. Wysyłka idzie w kolejce, jedna
        wiadomość co ok. 2 sekundy. Pojedynczej osobie odpowiesz przyciskiem „Odpowiedz” w tabelach poniżej.
      </div>

      @if (status?.campaignId) {
        <div class="mailing-status">
          <div class="flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <strong>
                @if (status?.isRunning) {
                  Wysyłka w toku
                } @else if (status?.cancelled) {
                  Wysyłka przerwana
                } @else {
                  Ostatnia wysyłka zakończona
                }
              </strong>
              <span class="muted">
                — „{{ status?.subject }}”, {{ status?.startedAt | date: 'yyyy-MM-dd HH:mm' }}
                @if (status?.startedBy) {
                  , {{ status?.startedBy }}
                }
              </span>
            </div>
            @if (status?.isRunning) {
              <p-button
                label="Przerwij"
                icon="pi pi-stop"
                size="small"
                severity="danger"
                [outlined]="true"
                [loading]="cancelling"
                (onClick)="cancel()"
                data-clarity-unmask="true"
              />
            }
          </div>
          <p-progressBar [value]="progressPercent" [showValue]="false" styleClass="mt-2 mailing-progress" />
          <div class="muted mt-1">
            Wysłano {{ status?.sent }} z {{ status?.total }}
            @if (status?.failed) {
              · nieudane: {{ status?.failed }}
            }
            @if (status?.pending) {
              · w kolejce: {{ status?.pending }}
            }
          </div>
          @if (status?.errors?.length) {
            <ul class="mailing-errors">
              @for (error of status?.errors; track error) {
                <li>{{ error }}</li>
              }
            </ul>
          }
        </div>
      }
    </div>

    <div class="section-title">
      <i class="pi pi-envelope"></i>
      <span>Adresy ze zgłoszeń</span>
    </div>
    @if (reports.recipients.length) {
      <div class="table-wrapper">
        <p-table [value]="reports.recipients" styleClass="p-datatable-sm" [scrollable]="true">
          <ng-template pTemplate="header">
            <tr>
              <th>E-mail</th>
              <th>Zgłoszenia</th>
              <th>Ostatnie zgłoszenie</th>
              <th>Ostatnie powiadomienie</th>
              <th>Status</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-recipient>
            <tr>
              <td>{{ recipient.email }}</td>
              <td data-label="Zgłoszenia">{{ recipient.reportsCount }}</td>
              <td data-label="Ostatnie zgłoszenie">{{ recipient.lastReportAt | date: 'yyyy-MM-dd HH:mm' }}</td>
              <td data-label="Ostatnie powiadomienie">{{ recipient.lastNotifiedAt ? (recipient.lastNotifiedAt | date: 'yyyy-MM-dd HH:mm') : '—' }}</td>
              <td data-label="Status">
                <p-tag
                  [value]="
                    recipient.awaitingNotification
                      ? 'czeka'
                      : recipient.lastNotificationWasReply
                        ? 'odpowiedziano'
                        : 'powiadomiony'
                  "
                  [severity]="recipient.awaitingNotification ? 'warning' : 'success'"
                />
              </td>
              <td class="actions">
                <p-button
                  label="Odpowiedz"
                  icon="pi pi-reply"
                  size="small"
                  [text]="true"
                  (onClick)="openReply(recipient.email)"
                  data-clarity-unmask="true"
                />
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    } @else {
      <div class="empty">Brak zgłoszeń z adresem e-mail w wybranym okresie.</div>
    }

    <div class="section-title">
      <i class="pi pi-comments"></i>
      <span>Zgłoszenia z aplikacji</span>
    </div>
    @if (reports.feedback.length) {
      <div class="table-wrapper">
        <p-table
          [value]="reports.feedback"
          styleClass="p-datatable-sm"
          [scrollable]="true"
          [paginator]="reports.feedback.length > 20"
          [rows]="20"
        >
          <ng-template pTemplate="header">
            <tr>
              <th>Data</th>
              <th>E-mail</th>
              <th>Powód</th>
              <th>Urządzenie</th>
              <th>Wiadomość</th>
              <th>Ostatni błąd</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-item>
            <tr>
              <td class="nowrap">{{ item.receivedAt | date: 'yyyy-MM-dd HH:mm' }}</td>
              <td data-label="E-mail"><app-user-account-link [email]="item.email" [userGuid]="item.userGuid" /></td>
              <td data-label="Powód">{{ reasonLabel(item.reason) }}</td>
              <td class="nowrap" data-label="Urządzenie">
                @if (item.clientDevice) {
                  <app-client-device [device]="item.clientDevice" />
                } @else {
                  {{ item.platform || '—' }}
                  @if (item.appVersion) {
                    <span class="muted">{{ item.appVersion }}</span>
                  }
                }
              </td>
              <td class="message" data-label="Wiadomość">{{ item.message }}</td>
              <td class="mono" data-label="Ostatni błąd">{{ item.lastError || '—' }}</td>
              <td class="actions" data-label="Odpowiedź">
                @if (item.email) {
                  <span class="actions-row">
                    @if (answeredAt(item); as answered) {
                      <i
                        class="pi pi-check-circle answered"
                        [pTooltip]="'Wysłaliśmy wiadomość po tym zgłoszeniu: ' + (answered | date: 'yyyy-MM-dd HH:mm')"
                        tooltipPosition="left"
                      ></i>
                    }
                    <p-button
                      icon="pi pi-reply"
                      size="small"
                      [text]="true"
                      [rounded]="true"
                      pTooltip="Odpowiedz na to zgłoszenie"
                      tooltipPosition="left"
                      (onClick)="openReply(item.email, item)"
                      data-clarity-unmask="true"
                    />
                  </span>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    } @else {
      <div class="empty">Brak zgłoszeń w wybranym okresie.</div>
    }

    <div class="section-title">
      <i class="pi pi-database"></i>
      <span>Awarie bazy danych</span>
    </div>
    @if (reports.databaseOutages.length) {
      <div class="table-wrapper">
        <p-table [value]="reports.databaseOutages" styleClass="p-datatable-sm" [scrollable]="true">
          <ng-template pTemplate="header">
            <tr>
              <th>Początek</th>
              <th>Koniec</th>
              <th>Czas</th>
              <th>Instancja</th>
              <th>Błędów</th>
              <th>Ostatni błąd</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-outage>
            <tr>
              <td class="nowrap">{{ outage.startedAt | date: 'yyyy-MM-dd HH:mm' }}</td>
              <td class="nowrap" data-label="Koniec">
                @if (outage.recoveredAt) {
                  {{ outage.recoveredAt | date: 'yyyy-MM-dd HH:mm' }}
                } @else {
                  <p-tag
                    value="brak zapisu o przywróceniu"
                    severity="danger"
                    pTooltip="Awaria trwa albo instancja została zrestartowana, zanim baza wróciła."
                  />
                }
              </td>
              <td class="nowrap" data-label="Czas">{{ outageDuration(outage.startedAt, outage.recoveredAt ?? outage.lastFailureAt) }}</td>
              <td data-label="Instancja">{{ outage.instance || '—' }}</td>
              <td data-label="Błędów">{{ outage.occurrences }}</td>
              <td class="message" data-label="Ostatni błąd" [pTooltip]="outage.exceptionType">{{ outage.lastMessage || '—' }}</td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    } @else {
      <div class="empty">Brak zapisanych awarii bazy danych w wybranym okresie.</div>
    }

    <div class="data-directory muted">Pliki: <span class="mono">{{ reports.dataDirectory }}</span></div>
  }
</div>

<app-reply-dialog
  header="Odpowiedź na zgłoszenie"
  [(visible)]="replyVisible"
  [email]="replyTarget?.email"
  [userGuid]="replyTarget?.userGuid"
  [thread]="replyTarget?.thread ?? []"
  [(subject)]="replySubject"
  [(body)]="replyBody"
  hint="Wiadomość wyjdzie od razu. Ta osoba nie dostanie potem wiadomości zbiorczej, dopóki nie zgłosi kolejnego problemu."
  [sending]="replying"
  [error]="replyError"
  (send)="sendReply()"
/>
